import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { AppConfig } from "../../../api/config/config";
import FeeIcon from "../../../assets/images/icons/blue-bank-note-02.svg";
import ShareIcon from "../../../assets/images/icons/shareIcon.svg";
import {
  GeneralButton,
  Loader,
  ProfileAvatar,
  ShareLinkComponent,
} from "../../../components";
import { useAppMediaQuery } from "../../../hooks";
import { IServiceProvider } from "../../../interface/ProfileInterfaces";
import { TitleSideLink } from "../../../Share";

const ProfileSidebar: React.FC<{
  profileData?: IServiceProvider;
  isLoading: boolean;
}> = ({ profileData, isLoading }) => {
  const { t } = useTranslation();

  const [isShareOpen, setIsShareOpen] = useState<boolean>(false);
  const location = useLocation();
  const currentLocationParams = new URLSearchParams(location.search);
  const provider = currentLocationParams.get("provider");

  const isDoctorProfile = provider != "0" && !!provider;
  const { isMobileOrTablet } = useAppMediaQuery();

  const SpecialtyNames = `${profileData?.Specialties?.map((item: { Name: string }) => item?.Name)}`;

  const links = [
    ...(isDoctorProfile
      ? [
          {
            route: `certificates?provider=${provider}`,
            name: isMobileOrTablet
              ? t("certificates")
              : t("doctor_certificates"),
          },
        ]
      : []),
    {
      route: `contact?provider=${provider}`,
      name: t("contact_info"),
    },
    {
      route: `location?provider=${provider}`,
      name: isMobileOrTablet ? t("location") : t("location_info"),
    },
  ];

  return (
    <div className="box flex h-full w-full flex-col lg:w-1/3">
      {isLoading ? (
        <div className="m-auto h-full w-full">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-6">
            <div className="flex justify-between">
              <div className="flex items-start gap-3">
                <div className="relative">
                  <ProfileAvatar
                    Id={Number(profileData?.Id)}
                    alt="Profile"
                    src={profileData?.Image}
                    className="border-primary-bg bg-primary-bg h-14 w-14 rounded-full border"
                    FullName={profileData?.Name}
                  />
                  {!isDoctorProfile && (
                    <div
                      className={`absolute bottom-0 end-0 h-8 w-8 border-8 border-white ${profileData?.IsOpen ? "bg-successGreen" : "bg-error"} rounded-[50%]`}
                    />
                  )}
                </div>
                <div className="text-body-text text-start">
                  <h3 className="text-primary-text text-lg font-bold">
                    {profileData?.Name}
                  </h3>
                  <p className="text-primary-bgGray text-sm">
                    {isDoctorProfile ? (
                      <ul>{SpecialtyNames}</ul>
                    ) : (
                      profileData?.FacilityType?.Name
                    )}
                  </p>
                </div>
              </div>
              <GeneralButton
                color="lite"
                icon={ShareIcon}
                title={t("share")}
                className="!text-primary-text h-fit w-fit border"
                onClick={() => setIsShareOpen(true)}
              />
            </div>
            {isDoctorProfile && (
              <div className="text-body-text text-wrap text-sm">
                {profileData?.Bio}
              </div>
            )}
          </div>
          {!!profileData?.Tarrif && (
            <>
              <hr />
              <div className="flex justify-between py-4">
                <div className="text-base">{t("session_price")}</div>
                <div className="flex gap-2 text-nowrap text-base font-bold text-primary">
                  <img src={FeeIcon} alt="fee" />
                  {<span>{`${profileData?.Tarrif} ${t("syp")}`}</span>}
                </div>
              </div>
              <hr />
            </>
          )}
          <ShareLinkComponent
            shareLink={`${AppConfig.WEB_APP_URL}${location?.pathname + location?.search}`}
            open={isShareOpen}
            onClose={() => {
              setIsShareOpen(false);
            }}
          />
          <div className="mt-4 flex flex-col gap-1">
            {links?.map((e, i) => (
              <TitleSideLink key={i} label={e.name} to={e.route} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSidebar;
